import { DateTime } from 'luxon';
import { ApolloCache } from '@apollo/client';
import { SearchCriteria } from 'modules/notes/common';
import { dateRangeTag } from 'modules/notes/common/components/SearchFacets/DateRangeFacet';
import { LabelValueType } from 'modules/notes/common/components/SearchFacets/LabelFacetDetails';
import {
  CreateTimeSignalMutation,
  LabelValue,
  TimeSignal,
  TimeSignalClassification,
  TimeSignalsDocument,
  TimeSignalsQuery,
  UpdateTimeSignalsMutation,
  MergeTimeSignalsMutation,
  SortOrder,
  LabelValueAcceptedBy,
  LabelValueSource,
  LabelSuggestionsResult,
  UpdateTimeSignalMutation
} from 'graphql/types';
import {
  TimeSignalGetParam,
  ALL_CLASSIFICATIONS
} from 'modules/notes/hooks/useTimeSignalParam';
import { NotesClassification } from 'modules/notes/NotesFilterContextProvider';

export const isSearchCriteriaMatch = (
  timeSignal: TimeSignal,
  searchCriteria: SearchCriteria
): boolean => {
  const signalLabelValuesById = timeSignal.labelValues.reduce((acc, label) => {
    if (label.label.id) {
      acc[label.label.id] = label.value;
    }
    return acc;
  }, {} as Record<string, LabelValue | null | undefined>);

  const filterLabelValuesById = Object.keys(searchCriteria?.criterions ?? {})
    .filter((key) => key !== dateRangeTag)
    .reduce((acc, key) => {
      acc.push(...(searchCriteria.criterions[key] as LabelValueType[]));
      return acc;
    }, [] as LabelValueType[])
    .reduce((acc, item) => {
      if (!acc[item.labelId]) {
        acc[item.labelId] = [];
      }
      acc[item.labelId].push(item);
      return acc;
    }, {} as Record<string, LabelValueType[]>);

  const isLabelsMatch = Object.keys(filterLabelValuesById).every((key) => {
    const filterLabel = filterLabelValuesById[key];
    const labelValue = signalLabelValuesById[key];
    if (!labelValue && (!filterLabel || !filterLabel.length)) {
      return true;
    }

    if (
      filterLabel.find((l) => {
        if (l.values?.length) {
          return l.values.find((val) => val.id === labelValue?.idValue);
        }
        return labelValue?.idValue === l.id;
      })
    ) {
      return true;
    }
    return false;
  });

  return isLabelsMatch;
};

export const canAddNoteToCache = (
  param: TimeSignalGetParam,
  currentClassification: NotesClassification,
  timeSignal: TimeSignal,
  searchCriteria: SearchCriteria
): boolean => {
  if (
    currentClassification === 'All' &&
    !ALL_CLASSIFICATIONS.includes(timeSignal.classification)
  ) {
    return false;
  } else if (
    currentClassification !== 'All' &&
    TimeSignalClassification[currentClassification] !==
      timeSignal.classification
  ) {
    return false;
  }

  if (
    timeSignal.date < param.input.filter?.fromDate ||
    timeSignal.date > param.input.filter?.toDate
  ) {
    return false;
  }
  if (!isSearchCriteriaMatch(timeSignal, searchCriteria)) {
    return false;
  }
  const searchText =
    timeSignal.labelValues.reduce((acc, label) => {
      return label.value?.stringValue ? acc + label.value.stringValue : acc;
    }, '') + timeSignal.description;

  const keywords =
    param.input.filter?.searchText?.toLowerCase().split(/\s+/) ?? [];
  return keywords.length === 0
    ? true
    : keywords.every((key) => searchText.toLowerCase().includes(key));
};

export const shouldAddToCache = (
  existingSignals: TimeSignalsQuery,
  timeSignal: TimeSignal,
  sortOrder: SortOrder
): boolean => {
  const lastLoadedNote =
    existingSignals?.timeSignals?.items[
      existingSignals?.timeSignals?.items.length - 1
    ] ?? [];

  const lastLoadedNoteDate = lastLoadedNote.date;
  const lastLoadedNoteStartTime = DateTime.fromISO(lastLoadedNote.startTime);
  const lastLoadedNoteEndTime = DateTime.fromISO(lastLoadedNote.endTime);
  const timeSignalStartTime = DateTime.fromISO(timeSignal.startTime);
  const timeSignalEndTime = DateTime.fromISO(timeSignal.endTime);
  if (
    lastLoadedNoteDate > timeSignal.date &&
    existingSignals?.timeSignals.cursor
  )
    return false;
  else if (
    lastLoadedNoteDate === timeSignal.date &&
    existingSignals?.timeSignals.cursor
  ) {
    if (
      sortOrder === SortOrder.Ascending &&
      (!timeSignalStartTime.isValid ||
        timeSignalStartTime > lastLoadedNoteStartTime ||
        (timeSignalStartTime.equals(lastLoadedNoteStartTime) &&
          timeSignalEndTime > lastLoadedNoteEndTime))
    )
      return false;
    else if (
      sortOrder === SortOrder.Descending &&
      ((timeSignalStartTime.isValid && !lastLoadedNoteStartTime.isValid) ||
        timeSignalStartTime < lastLoadedNoteStartTime ||
        (timeSignalStartTime.equals(lastLoadedNoteStartTime) &&
          timeSignalEndTime < lastLoadedNoteEndTime))
    )
      return false;
  }
  return true;
};
export const sortedTimeSignals = (
  items: TimeSignal[],
  sortOrder: SortOrder,
  timeSignal: TimeSignal
): TimeSignal[] => {
  const startTime = DateTime.fromISO(timeSignal.startTime);
  const endTime = DateTime.fromISO(timeSignal.endTime);
  const lessThanTimeSignal = items.filter((item) => {
    const itemStartTime = DateTime.fromISO(item.startTime);
    const itemEndTime = DateTime.fromISO(item.endTime);
    if (
      itemStartTime < startTime ||
      (itemStartTime.equals(startTime) && itemEndTime <= endTime) ||
      !startTime.isValid
    ) {
      return item;
    }
  });
  const greaterThanTimeSignal = items.filter((item) => {
    const itemStartTime = DateTime.fromISO(item.startTime);
    const itemEndTime = DateTime.fromISO(item.endTime);
    if (
      itemStartTime > startTime ||
      (itemStartTime.equals(startTime) && itemEndTime > endTime) ||
      (!itemStartTime.isValid && startTime.isValid)
    ) {
      return item;
    }
  });
  const sortedTimeSignals =
    sortOrder === SortOrder.Ascending
      ? [...lessThanTimeSignal, timeSignal, ...greaterThanTimeSignal]
      : [...greaterThanTimeSignal, timeSignal, ...lessThanTimeSignal];
  return sortedTimeSignals;
};

export const appendTimeSignals = (
  existingSignals: TimeSignalsQuery,
  timeSignal: TimeSignal,
  sortOrder: SortOrder
): TimeSignal[] => {
  const date = DateTime.fromISO(timeSignal.date);
  const beforeItems = existingSignals.timeSignals.items?.filter(
    (s) => DateTime.fromISO(s.date) > date
  );

  const afterItems = existingSignals.timeSignals.items?.filter(
    (s) => DateTime.fromISO(s.date) < date
  );

  const items = existingSignals.timeSignals.items?.filter(
    (s) => DateTime.fromISO(s.date).equals(date) && s.id !== timeSignal.id
  );
  const sortedSignals = sortedTimeSignals(items, sortOrder, timeSignal);
  return [...beforeItems, ...sortedSignals, ...afterItems];
};

export const updateCreateSignalCache = (
  cache: ApolloCache<CreateTimeSignalMutation | MergeTimeSignalsMutation>,
  timeSignal: TimeSignal,
  param: TimeSignalGetParam,
  currentClassification: NotesClassification,
  searchCriteria: SearchCriteria,
  sortOrder: SortOrder
): void => {
  if (
    param.input.filter?.classifications?.includes(timeSignal.classification)
  ) {
    const existingSignals: TimeSignalsQuery | null = cache.readQuery({
      query: TimeSignalsDocument,
      variables: param
    });

    if (existingSignals) {
      const canAddNote = canAddNoteToCache(
        param,
        currentClassification,
        timeSignal,
        searchCriteria
      );
      const shouldAdd = shouldAddToCache(
        existingSignals,
        timeSignal,
        sortOrder
      );
      const orderedSignal = appendTimeSignals(
        existingSignals,
        timeSignal,
        sortOrder
      );

      cache.writeQuery({
        query: TimeSignalsDocument,
        variables: param,
        data: {
          timeSignals: canAddNote
            ? shouldAdd
              ? {
                  __typename: 'TimeSignalsResult',
                  items: [...orderedSignal],
                  cursor: existingSignals.timeSignals.cursor,
                  totalCount: existingSignals.timeSignals.totalCount + 1
                }
              : {
                  ...existingSignals.timeSignals,
                  totalCount: existingSignals.timeSignals.totalCount + 1
                }
            : existingSignals.timeSignals
        }
      });
    }
  }
};

export const updateCachedTimeSignalsWithFederatedSuggestions = (
  cache: ApolloCache<CreateTimeSignalMutation | UpdateTimeSignalMutation>,
  federatedSuggestions: LabelSuggestionsResult,
  param: TimeSignalGetParam
): void => {
  const existingSignals: TimeSignalsQuery | null = cache.readQuery({
    query: TimeSignalsDocument,
    variables: param
  });

  if (existingSignals?.timeSignals?.items) {
    const signalsCopy = [...existingSignals.timeSignals.items];
    const index = signalsCopy.findIndex(
      (signal: TimeSignal) =>
        signal.labelValuesInferenceId === federatedSuggestions.inferenceId
    );
    const matchingSignal = signalsCopy[index];
    const newSignal = {
      ...matchingSignal,
      suggestions: federatedSuggestions
    };
    signalsCopy[index] = newSignal;

    if (matchingSignal && federatedSuggestions) {
      cache.writeQuery({
        query: TimeSignalsDocument,
        variables: param,
        data: {
          timeSignals: {
            __typename: 'TimeSignalsResult',
            ...existingSignals.timeSignals,
            items: signalsCopy
          }
        }
      });
    }
  }
};

export const updateTimeSignalsCache = (
  cache: ApolloCache<UpdateTimeSignalsMutation | MergeTimeSignalsMutation>,
  signals: TimeSignal[],
  param: TimeSignalGetParam,
  selectedClassification: NotesClassification,
  classificationTarget: TimeSignalClassification,
  useLabelAcceptedBy: boolean
): void => {
  const timeSignalsToRemove = signals.map((s) => s.id);
  const existingSignals: TimeSignalsQuery | null = cache.readQuery({
    query: TimeSignalsDocument,
    variables: param
  });

  if (existingSignals) {
    let updateSignals;
    if (
      selectedClassification === 'All' &&
      classificationTarget === TimeSignalClassification.Accepted
    ) {
      updateSignals = (existingSignals!.timeSignals.items || []).map(
        (signal) => {
          if (timeSignalsToRemove.includes(signal.id)) {
            const labelValues = signal.labelValues.map((item) => {
              if (item.value) {
                return useLabelAcceptedBy
                  ? {
                      ...item,
                      acceptedBy: LabelValueAcceptedBy.User,
                      valueSource: item.valueSource ?? LabelValueSource.User
                    }
                  : { ...item, valueSource: LabelValueSource.User };
              } else return item;
            });
            return {
              ...signal,
              classification: classificationTarget,
              labelValues: labelValues
            };
          } else return signal;
        }
      );
    } else if (
      selectedClassification === 'All' &&
      classificationTarget !== TimeSignalClassification.Trash
    ) {
      updateSignals = (existingSignals!.timeSignals.items || []).map((signal) =>
        timeSignalsToRemove.includes(signal.id)
          ? { ...signal, classification: classificationTarget }
          : signal
      );
    } else {
      updateSignals = (existingSignals!.timeSignals.items || []).filter(
        (s) => !timeSignalsToRemove.includes(s.id)
      );
    }

    const totalCount =
      selectedClassification === 'All' &&
      classificationTarget !== TimeSignalClassification.Trash
        ? existingSignals.timeSignals.totalCount
        : existingSignals.timeSignals.totalCount - timeSignalsToRemove.length;

    cache.writeQuery({
      query: TimeSignalsDocument,
      variables: param,
      data: {
        timeSignals: {
          __typename: 'TimeSignalsResult',
          items: updateSignals,
          totalCount,
          cursor: existingSignals.timeSignals?.cursor
        }
      }
    });
  }
};

export const isSortRequired = (
  existingSignals: TimeSignalsQuery,
  timeSignal: TimeSignal,
  sortOrder: SortOrder | undefined | null
): boolean => {
  const dateArray = existingSignals.timeSignals.items.filter(
    (t) => t.date === timeSignal.date
  );
  const i = dateArray.findIndex((t) => t.id === timeSignal.id);
  const t = sortOrder === SortOrder.Descending ? 1 : -1;
  const previousElem = dateArray[i - t];
  const nextElem = dateArray[i + t];

  if (dateArray.length === 1) return false;
  if (
    timeSignal.startTime === null &&
    (!previousElem || !previousElem.startTime)
  )
    return false;
  if (
    (!previousElem ||
      previousElem?.startTime > timeSignal?.startTime ||
      !previousElem?.startTime ||
      (previousElem?.startTime === timeSignal?.startTime &&
        previousElem?.endTime > timeSignal?.endTime)) &&
    (!nextElem ||
      timeSignal?.startTime > nextElem?.startTime ||
      (timeSignal?.startTime === nextElem?.startTime &&
        timeSignal?.endTime > nextElem?.endTime))
  )
    return false;
  return true;
};
